import { Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogContentText, IconButton, InputBase, ListItemText, makeStyles, Menu, MenuItem, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip } from "@material-ui/core";
import { blue } from "@material-ui/core/colors";
import CheckIcon from '@material-ui/icons/Check';
import CloseIcon from '@material-ui/icons/Close';
import SearchIcon from '@material-ui/icons/Search';
import ViewColumnIcon from '@material-ui/icons/ViewColumn';
import clsx from "clsx";
import { forwardRef, memo, Suspense, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { atom, atomFamily, useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import { useAlert } from "../../hooks/useAlert";
import useEffectSkipFirst from "../../hooks/useEffectSkipFirst";
import { Loading } from "../../parts/Loading";
import { OnClickAnchor } from "../../parts/OnClickAnchor";
import { aircraftGroupExternalListFilterWordState, aircraftGroupExternalSelectedIdState, aircraftListFilterWordState, useAircraftGroupExternalLinkageAircraft, useLinkableSourceList } from "../../store/AircraftSettingState";
import { coPilotListFilterWordState } from "../../store/CoPilotSettingState";
import { userState } from "../../store/UserState";
import { SUBJ_AIRCRAFT_GROUP_EXTERNAL_LINKAGE_AIRCRAFT, VERB_UPDATE } from "../../utils/AccessControl";
import RoleControl from "../RoleControl";
import { AircraftSettingMenu } from "./AircraftSetting";

const useStyles = makeStyles({
	container: {
		height: '100%',
		// border: '1px solid #a7a7a7',
	},
	stickyTable: {
		borderCollapse: 'separate',
		marginBottom: '1px',
	},
	fixedCell: {
		position: 'sticky',
		left: 0,
	},
	tableCell: {
		backgroundColor: 'white',
		borderRight: '1px solid #a7a7a7',
		borderBottom: '1px solid #a7a7a7',
		'th&': {
			borderTop: '1px solid #a7a7a7',
		},
		'&:nth-child(1)': {
			position: 'sticky',
			left: 0,
			zIndex: 2,
			borderLeft: '1px solid #a7a7a7',
		},
		// '&:nth-child(2)': {
		// 	position: 'sticky',
		// 	left: '34px',
		// 	zIndex: 2,
		// 	whiteSpace: 'nowrap',
		// },
		verticalAlign: 'baseline',
	},
	headerCell: {
		color: '#fff',
		backgroundColor: '#4c4f53',
		whiteSpace: 'nowrap',
		position: 'sticky',
		zIndex: 3,
		top: 0,
		verticalAlign: 'middle',
		'&:nth-child(-n+1)': {
			zIndex: 4
		},
		'tr:nth-child(2) &': {
			zIndex: 2,
			top: 38,
			borderTop: 'none',
			borderLeft: 'none',
		},
		'& a:link, a:visited': {
			color: '#fff',
		},
	},
	imageCell: {
		padding: '4px',
		width: '72px',
		boxSizing: 'border-box',
		'& img': {
			display: 'block',
			margin: '0 auto',
		}
	},
	alignRight: {
		textAlign: 'right',
	},
	svgCell: {
		verticalAlign: 'bottom',
	},
	filterBox: {
		padding: '2px 4px',
		display: 'flex',
		alignItems: 'center',
		width: 400,
		marginBottom: 1,
		marginRight: 'auto',
	},
	input: {
		marginLeft: 8,
		flex: 1,
		"& .MuiInputBase-input": {
			padding: '3px 0 4px',
		}
	},
	iconButton: {
		padding: 1,
	},
	closeButton: {
		padding: 2,
	},
	searchIcon: {
		color: 'rgba(0, 0, 0, 0.54)',
	},
	formInputText: {
		'& .MuiFormLabel-root': {
			color: 'black',
			'&.Mui-error': {
				color: '#f44336',
			}
		},
		'&:not(:first-child)': {
			marginTop: '20px',
		},
		'& .required': {
			color: '#f44336',
		}
	},
	commission: {
		backgroundColor: '#d9ead3ff',
	},
	agreement: {
		backgroundColor: '#f4ccccff',
	},
	sharedCell: {
		height: 0,
	},
	headerGroupCell: {
		paddingRight: 10,
		'& a:link, a:visited': {
			color: 'revert',
		},
	},
	checkCell: {
		verticalAlign: 'middle',
		transition: 'background-color 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
	},
	checkIcon: {
		width: 24,
		height: 24,
		margin: '0 auto',
		'& .MuiSvgIcon-root': {
			color: 'rgb(2, 136, 209)',
		}
	},
	modifiedCell: {
		backgroundColor: 'orange',
	},
	smallCheckbox: {
		padding: 2,
	},
	hidden: {
		visibility: 'hidden',
	},
	filterIconBox: {
		position: 'absolute',
		right: 0,
		marginTop: '-43px',
		marginRight: '10px',
		zIndex: 10,
	},
	filterIcon: {
		padding: 6,
		color: '#a7a7a7',
	},
	filterIconFiltered: {
		color: blue[400],
	},
	newline: {
		whiteSpace: 'break-spaces',
	},
	differenceList: {
		marginLeft: '1em',
	},
});

const checkAllAircraftGroupExternalState = atomFamily({
	key: 'checkAllAircraftGroupExternalState',
	default: false,
});

const resetAircraftGroupExternalState = atom({
	key: 'resetAircraftGroupExternalState',
	default: 0,
});

const columns = [
	{ id: 'aircraft_code', label: 'Aircraft Code' },
	{ id: 'aircraft_name', label: 'Nickname' },
	{ id: 'customer_name', label: 'Operating Company' },
	{ id: 'sbd_imei', label: 'SBD IMEI' },
	{ id: 'data_source', label: 'External Source' },
	{ id: 'aircraftgroup_name', label: 'Aircraft Group' },
];

const SelectCheckboxList = (props) => {
	const { title, dataList, enableList, setEnableList, icon } = props;
	const [open, setOpen] = useState(false);
	const [anchorEl, setAnchorEl] = useState(null);

	const handleClickIconButton = (event) => {
		setAnchorEl(event.currentTarget);
		setOpen(prev => !prev);
	}

	const handleClickAway = () => {
		setOpen(false);
	}

	const handleCheckboxChange = (agid) => {
		setEnableList(prev => {
			return {
				...prev,
				[agid]: !prev[agid]
			};
		});
	}

	const classes = useStyles();

	return (
		<>
			<Tooltip title={title}>
				<IconButton onClick={handleClickIconButton} className={clsx(classes.filterIcon, Object.values(enableList).length !== Object.values(enableList).filter(v => v).length && classes.filterIconFiltered)}>
					{icon}
				</IconButton>
			</Tooltip>
			<Menu
				open={open}
				anchorEl={anchorEl}
				onClose={handleClickAway}
			>
				{dataList.map((row, index) => {
					return (
						<MenuItem dense key={`SelectCheckboxList-${row.text}-${index}`} onClick={() => handleCheckboxChange(row.aircraftgroup_id)}>
							<Checkbox checked={enableList[row.aircraftgroup_id]} size='small'></Checkbox>
							<ListItemText>{row.aircraftgroup_name}</ListItemText>
						</MenuItem>
					)
				})}
			</Menu>
		</>
	)
};

const AircraftGroupListTableHeaderCell = memo((props) => {
	const { aircraftGroupId, aircraftGroupName, aircraftGroupType, editMode, editable, handleClickAircraftGroup } = props;
	const reset = useRecoilValue(resetAircraftGroupExternalState);
	const [checkAll, setCheckAll] = useRecoilState(checkAllAircraftGroupExternalState(aircraftGroupId));

	useEffect(() => {
		setCheckAll(false);
	}, [reset, setCheckAll]);

	const handleCheck = useCallback((value) => {
		setCheckAll(value);
	}, [setCheckAll]);

	const classes = useStyles();

	return (
		<TableCell
			style={{ minWidth: 0 }}
			className={clsx(
				classes.headerCell,
				classes.tableCell,
				classes.headerGroupCell,
				aircraftGroupType === 1 && classes.commission,
				aircraftGroupType === 2 && classes.agreement,
			)}
		>
			<OnClickAnchor onClick={() => handleClickAircraftGroup(aircraftGroupName)}>{aircraftGroupName}</OnClickAnchor>
			<Checkbox
				checked={checkAll}
				onChange={(event) => handleCheck(event.target.checked)}
				className={clsx(classes.smallCheckbox, !(editable && editMode) && classes.hidden)}
			/>
		</TableCell>
	);
});

const AircraftGroupListTableCell = memo(forwardRef((props, ref) => {
	const { aircraftCode, aircraftGroupId, initCheck, editMode, editable } = props;
	const reset = useRecoilValue(resetAircraftGroupExternalState);
	const checkAll = useRecoilValue(checkAllAircraftGroupExternalState(aircraftGroupId));
	const [check, setCheck] = useState(() => {
		if (ref.current?.[aircraftCode]?.[aircraftGroupId] !== undefined) {
			return ref.current[aircraftCode][aircraftGroupId];
		}
		return initCheck;
	});

	useEffectSkipFirst(() => {
		setCheck(initCheck);
	}, reset);

	useEffectSkipFirst(() => {
		if (editable && editMode) {
			handleCheck(checkAll);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, checkAll);

	const handleCheck = useCallback((value) => {
		setCheck(value);
		ref.current[aircraftCode] = Object.assign(ref.current[aircraftCode] || {}, {[aircraftGroupId]: value});
	}, [aircraftCode, aircraftGroupId, ref]);

	const classes = useStyles();

	return (
		<TableCell
			align="center"
			padding="checkbox"
			style={{ minWidth: 0 }}
			className={clsx(classes.tableCell, classes.checkCell, editMode && initCheck !== check && classes.modifiedCell)}
		>
			{!(editable && editMode) ?
				check ?
					<Box className={clsx(classes.checkIcon)}>
						<CheckIcon color="secondary" />
					</Box>
					:
					<></>
				:
				<Checkbox checked={check} onChange={(event) => handleCheck(event.target.checked)} />
			}
		</TableCell>
	)
}));

const ExternalSourceListTableRow = memo(forwardRef((props, ref) => {
	const { aircraftCode, linkableSourceList, linkedDataSourceId } = props;

	const classes = useStyles();

	return (
		linkableSourceList.map((column, i) => (
			<TableCell
				key={`cell-${aircraftCode}-${column.data_source_id}`}
				align="center"
				padding="checkbox"
				style={{ minWidth: 0 }}
				className={clsx(classes.tableCell, classes.checkCell)}
			>
				{linkedDataSourceId?.includes(column.data_source_id) &&
					<Box className={clsx(classes.checkIcon)}>
						<CheckIcon color="secondary" />
					</Box>
				}
			</TableCell>
		))
	);
}));

const AircraftGroupListTableRow = memo(forwardRef((props, ref) => {
	const { aircraftGroupList, aircraftCode, customerCode, initCheckList, editMode } = props;
	const user = useRecoilValue(userState);
	// const classes = useStyles();
	// const {t} = useTranslation();

	return (
		aircraftGroupList.map(group => (
			<AircraftGroupListTableCell
				key={`cell-${aircraftCode}-${group.aircraftgroup_id}`}
				aircraftCode={aircraftCode}
				aircraftGroupId={group.aircraftgroup_id}
				initCheck={!!initCheckList[group.aircraftgroup_id]}
				editMode={editMode}
				editable={group.is_editable && customerCode === user.customer}
				ref={ref}
			/>
		))
	);
}));

const AircraftGroupLinkageTable = memo(forwardRef(({filterWord, setWaiting, editMode, setEditMode, changeCoPilotSetting, changeAircraftSettingMenu, tableRef}, ref) => {
	const user = useRecoilValue(userState);
	const {
		aircraftList,
		aircraftGroupList,
		aircraftOfAircraftGroupsList,
		updateAircraftGroupLinkageAircraft,
		refresher,
	} = useAircraftGroupExternalLinkageAircraft();
	const {
		linkableSourceList
	} = useLinkableSourceList(true);
	const checkRef = useRef({});
	const [reset, setReset] = useRecoilState(resetAircraftGroupExternalState);
	const setAircraftListFileterWord = useSetRecoilState(aircraftListFilterWordState);
	const setCoPilotListFilterWord = useSetRecoilState(coPilotListFilterWordState);
	const setAircraftGroupListFilterWord = useSetRecoilState(aircraftGroupExternalListFilterWordState);
	const [aircraftGroupSelectedId, setAircraftGroupSelectedId] = useRecoilState(aircraftGroupExternalSelectedIdState);
	const [updateData, setUpdateData] = useState();
	const { showError, showWarning } = useAlert();

	const classes = useStyles();
	const {t} = useTranslation();

	const list = useCallback(() => {
		if (filterWord === "") {
			return aircraftList;
		}
		return aircraftList.filter(row => {
			return columns.some(col => {
				switch (col.id) {
					case 'sbd_imei':
						return (row[col.id] ? row[col.id] : t('Not associated')).indexOf(filterWord) !== -1;
					default:
						const cell = row[col.id] && ((col.format && col.format(row[col.id])) || row[col.id]);
						return cell !== undefined && cell !== null && String(cell).indexOf(filterWord) !== -1;
				}
			});
		});
	}, [aircraftList, filterWord, t]);

	const [enableAircraftGroups, setEnableAircraftGroups] = useState(() => {
		let check = true;
		if (aircraftGroupSelectedId) {
			check = false;
		}
		return Object.fromEntries(aircraftGroupList.map(group => {
			return [group.aircraftgroup_id, group.aircraftgroup_id === aircraftGroupSelectedId || check];
		}));
	});

	const filterdAircraftGroupList = useMemo(() => {
		return aircraftGroupList.filter(group => enableAircraftGroups[group.aircraftgroup_id]);
	}, [aircraftGroupList, enableAircraftGroups]);

	const checkList = useMemo(() => {
		const list = Object.fromEntries(Object.entries(aircraftOfAircraftGroupsList).map(([agid, amap]) =>
			[agid, Object.fromEntries(Object.entries(amap).map(([aid, adata]) =>
				[aid, !!adata.isvalid]))
			]
		));
		return list;
	}, [aircraftOfAircraftGroupsList]);

	useEffect(() => {
		return () => {
			setAircraftGroupSelectedId(null);
			refresher();
		}
	}, [refresher, setAircraftGroupSelectedId]);

	useImperativeHandle(ref, () => ({
		update: (updateData) => {
			setWaiting(true);
			updateAircraftGroupLinkageAircraft(updateData)
				.then(() => {
					// console.log("update comp");
				})
				.catch(e => {
					console.error("update error", e);
					if (e?.response?.status === 409 || e?.response?.status === 428) {
						showWarning(t('validate_optimistic_lock'));
						refresher();
					} else {
						showError(`${t('Update failed')}`);
					}
				})
				.finally(() => {
					setWaiting(false);
				});
		},
		check: (item, value) => {
			return aircraftGroupList.every(v => v[item] !== value);
		},
		save: handleSave,
		cancel: handleCancel,
	}));

	const handleSave = () => {
		const data = [];
		const warnAircrafts = [];
		Object.entries(checkRef.current).forEach(([aid, list]) => {
			Object.entries(list).forEach(([agid, valid]) => {
				const _data = aircraftOfAircraftGroupsList[agid][aid];
				if (_data?.isvalid !== valid) {
					if (valid) {
						data.push({
							aircraft_code: aid,
							aircraftgroup_id: agid,
							update_time: null,
						});
						if (_data?.customer_code_contractor !== null && _data?.customer_code_contractor !== user.customer) {
							warnAircrafts.push(_data);
						}
					} else {
						data.push({
							aircraft_code: aid,
							aircraftgroup_id: agid,
							update_by_user: _data.update_by_user,
							update_time: _data.update_time,
						});
					}
				}
			})
		});
		if (data.length > 0) {
			if (warnAircrafts.length > 0) {
				warnAircrafts.sort((a, b) => a.aircraft_code.localeCompare(b.aircraft_code));
				const message = (
					<>
						<DialogContentText className={classes.newline}>{t('AIRCRAFT_CONTRACTOR_DIFFERENT_WARNING')}</DialogContentText>
						<ul className={classes.differenceList}>
							{
								Array.from(new Set(warnAircrafts.map(v => t('AIRCRAFT_CONTRACTOR_DIFFERENT', { aircraft: v })))).map(v => (
									<li key={`key-diff-aircraft-${v.aircraft_code}`}>
										<DialogContentText style={{ marginBottom: 0 }}>{v}</DialogContentText>
									</li>
								))
							}
						</ul>
					</>
				);
				setUpdateData(data);
				setMessageAlert(message);
				setOpen(true);
			} else {
				setEditMode(false);
				ref.current.update(data);
			}
		} else {
			setEditMode(false);
		}
	}

	const handleCancel = () => {
		checkRef.current = {};
		setReset(!reset);
	}

	const handleClickAircraft = useCallback(aid => {
		if (aid) {
			setAircraftListFileterWord(aid);
		}
		changeAircraftSettingMenu(AircraftSettingMenu.AIRCRAFT);
	}, [changeAircraftSettingMenu, setAircraftListFileterWord]);

	const handleClickIMEI = useCallback(sbd_imei => {
		if (sbd_imei) {
			setCoPilotListFilterWord(sbd_imei);
		}
		changeCoPilotSetting();
	}, [changeCoPilotSetting, setCoPilotListFilterWord]);

	const handleClickAircraftGroup = useCallback(agid => {
		if (agid) {
			setAircraftGroupListFilterWord(agid);
		}
		changeAircraftSettingMenu(AircraftSettingMenu.AIRCRAFT_GROUP);
	}, [changeAircraftSettingMenu, setAircraftGroupListFilterWord]);

	// 確認ダイアログ
	const [open, setOpen] = useState(false);
	const [messageAlert, setMessageAlert] = useState('');

	const handleSaveOk = () =>{
		setOpen(false);
		setEditMode(false);
		ref.current.update(updateData);
	};

	const handleClose = () => {
		setOpen(false);
	};

	const handleKeyUp = e => {
		if (e.keyCode === 13) {
			handleSaveOk();
		}
	};
	
	return (
		<>
			<Box className={classes.filterIconBox}>
				<SelectCheckboxList
					title={t('Filter Column')}
					dataList={aircraftGroupList}
					enableList={enableAircraftGroups}
					setEnableList={setEnableAircraftGroups}
					icon={<ViewColumnIcon />}
				/>
			</Box>
			<TableContainer className={classes.container} ref={tableRef}>
				<Table aria-label="sticky table" size="small" className={classes.stickyTable}>
					<TableHead>
						<TableRow className={classes.tableRow}>
							{columns.map((column) => (
								column.id !== 'data_source' ?
									<TableCell
										key={column.id}
										align={column.align}
										style={{ minWidth: 0 }}
										className={clsx(classes.headerCell, classes.tableCell)}
										{...column.id === 'aircraftgroup_name' ? {colSpan: filterdAircraftGroupList.length} : {rowSpan: 2}}
									>
										{
											column.id === 'aircraft_code' ?
												<OnClickAnchor onClick={() => handleClickAircraft()}>{t(column.label)}</OnClickAnchor> :
											column.id === 'sbd_imei' ?
												<OnClickAnchor onClick={() => handleClickIMEI()}>{t(column.label)}</OnClickAnchor> :
											column.id === 'aircraftgroup_name' ? 
												<OnClickAnchor onClick={() => handleClickAircraftGroup()}>{t(column.label)}</OnClickAnchor> :
											t(column.label)
										}
									</TableCell>
								:
									linkableSourceList.map((column, i) => (
										<TableCell
											key={column.data_source_id}
											style={{ minWidth: 0 }}
											className={clsx(classes.headerCell, classes.tableCell)}
											rowSpan={2}
										>
											{t(column.data_source_name)}
										</TableCell>
									))
							))}
						</TableRow>
						<TableRow className={classes.tableRow}>
							{filterdAircraftGroupList.map(group => (
								<AircraftGroupListTableHeaderCell
									key={`head-${group.aircraftgroup_id}`}
									aircraftGroupId={group.aircraftgroup_id}
									aircraftGroupName={group.aircraftgroup_name}
									aircraftGroupType={group.aircraftgroup_type}
									editMode={editMode}
									editable={group.is_editable}
									handleClickAircraftGroup={handleClickAircraftGroup}
								/>
							))}
						</TableRow>
					</TableHead>
					<TableBody>
						{list().map((row) => {
							return (
								<TableRow hover role="checkbox" tabIndex={-1} key={row.aircraft_code}>
									{columns.map((column) => {
										const value = row[column.id];
										if (column.id === 'data_source') {
											return (
												<ExternalSourceListTableRow
													key={column.id}
													aircraftCode={row.aircraft_code}
													linkableSourceList={linkableSourceList}
													linkedDataSourceId={row.linked_data_source_id}
												/>
											);
										} else if (column.id !== 'aircraftgroup_name') {
											return (
												<TableCell
													key={column.id}
													align={column.align}
													className={clsx(classes.tableCell, column.id === 'aircraft_number' && classes.alignRight)}
												>
													{
														column.id === 'aircraft_code' && row.customer_code === user.customer ?
															<OnClickAnchor onClick={() => handleClickAircraft(row.aircraft_code)}>{value}</OnClickAnchor> :
														column.id === 'sbd_imei' && row.customer_code === user.customer ?
															value ? <OnClickAnchor onClick={() => handleClickIMEI(row.sbd_imei)}>{value}</OnClickAnchor> : t('Not associated') :
														column.format && typeof value === 'number' ? column.format(value) : value
													}
												</TableCell>
											);
										} else {
											return (
												<AircraftGroupListTableRow
													key={column.id}
													aircraftGroupList={filterdAircraftGroupList}
													aircraftCode={row.aircraft_code}
													customerCode={row.customer_code}
													initCheckList={Object.entries(checkList).reduce((a, [agid, v]) => {
														a[agid] = v[row.aircraft_code];
														return a;
													}, {})}
													editMode={editMode}
													ref={checkRef}
												/>
											);
										}
									})}
								</TableRow>
							);
						})}
					</TableBody>
				</Table>
			</TableContainer>
			<Dialog open={open} onClose={handleClose} onKeyUp={handleKeyUp}>
				<DialogContent>
					{messageAlert}
				</DialogContent>
				<DialogActions>
					<Button onClick={handleClose} color="primary">Cancel</Button>
					<Button onClick={handleSaveOk} color="primary">Ok</Button>
				</DialogActions>
			</Dialog>
		</>
	);
}));

function AircraftGroupExternalLinkageAircraft({changeCoPilotSetting, changeAircraftSettingMenu}) {
	const tableRef = useRef(null);
	const childRef = useRef({});
	const inputFilterRef = useRef(null);
	const [filterWord, setFileterWord] = useState('');
	const [editMode, setEditMode] = useState(false);
	const [waiting, setWaiting] = useState(false);

	const classes = useStyles();
	const {t} = useTranslation();

	useEffect(() => {
		return () => {
			setFileterWord('');
		}
	}, [setFileterWord]);

	const handleSave = () => {
		if (editMode) {
			childRef.current.save();
		} else {
			setEditMode(!editMode);
		}
	}

	const handleCancel = () => {
		childRef.current.cancel();
		setEditMode(false);
	}

	const handleSearch = () => {
		const word = inputFilterRef.current.value;
		setFileterWord(word);
		tableRef.current.scrollTop = 0;
	}

	const handleDeleteFilter = () => {
		const word = "";
		inputFilterRef.current.value = word;
		setFileterWord(word);
		tableRef.current.scrollTop = 0;
	}

	return (
		<Box style={{ position: 'relative', display: 'flex', flexDirection: 'column', height: '100%' }}>
			<Box style={{ display: 'flex', justifyContent: 'flex-end', padding: '10px 50px 10px 10px' }}>
				<Paper component="form" className={classes.filterBox} onSubmit={(event) => { handleSearch(event); event.preventDefault(); }}>
					<div className={classes.searchIcon}>
						<SearchIcon />
					</div>
					<InputBase
						className={classes.input}
						placeholder={`${t('Aircraft Code')}、${t('Nickname')}、${t('Operating Company')} ...`}
						inputProps={{ 'aria-label': 'filter aircraft group list' }}
						inputRef={inputFilterRef}
						defaultValue={filterWord}
						// onChange={handleChange}
					/>
					{filterWord.length > 0 && <IconButton type="button" className={classes.iconButton} aria-label="delete" onClick={handleDeleteFilter}>
						<CloseIcon />
					</IconButton>}
				</Paper>
				<Box style={{ display: 'flex', justifyContent: 'flex-end', flex: 1 }}>
					<RoleControl subject={SUBJ_AIRCRAFT_GROUP_EXTERNAL_LINKAGE_AIRCRAFT} verb={VERB_UPDATE}>
						{editMode ? <Button variant="outlined" size="small" className={classes.btn} style={{ marginRight: 20 }} onClick={handleCancel}>{t('Cancel')}</Button> : <div/>}
						<Button variant="outlined" size="small" className={classes.btn} onClick={handleSave}>{!editMode ? t('EDIT') : t('save')}</Button>
					</RoleControl>
				</Box>
			</Box>
			<Box style={{ padding: '0 10px 10px', flexGrow: 1, overflow: 'hidden' }}>
				{waiting && <Loading backdrop={true} />}
				<Suspense fallback={<Loading />}>
					<AircraftGroupLinkageTable filterWord={filterWord} ref={childRef} setWaiting={setWaiting} editMode={editMode} setEditMode={setEditMode} changeCoPilotSetting={changeCoPilotSetting} changeAircraftSettingMenu={changeAircraftSettingMenu} tableRef={tableRef}/>
				</Suspense>
			</Box>
		</Box>
	)
}

export default AircraftGroupExternalLinkageAircraft;
